import React, { memo, useCallback, useContext, useMemo } from "react";
import {
    isCompactScreenWidth,
    useQuickCheckoutNavHandlers,
    useScreenWidthSize,
} from "@swiggy-private/checkout-core";
import { format } from "@swiggy-private/js-utils";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as chevronUpFilled } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronUpFilled/12";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";

import { TurboPaymentContext } from "@payment-ui/components/turbo-payment-widget/TurboPaymentContext";
import { CHANGE_PAYMENT_CTA_ARIA_LABEL, SLIDE_TO_PAY_AMOUNT } from "@payment-ui/constants/TurboPayment";
import { getTurboPayExtraParams, getTurboPaymentPresentationalInfo } from "@payment-ui/helpers/TurboPayment";
import { IQuickCheckoutTurboPaymentMethodProps, TurboPaymentVariant } from "@payment-ui/interfaces";

import TurboPaymentDisplayName from "../../turbo-payment-display-name/v2";
import TurboPaymentMethodMetaWidget from "../../turbo-payment-method-meta-widget/v2";
import { QuickCheckoutTurboV2PaymentButton } from "../../turbo-v2-pay-button";
import {
    Container,
    Flex,
    PaymentIconV2,
    PaymentMethodContainer,
    PaymentMethodPayButtonContainer,
    StyledText,
} from "./styles";

/**
 * Quick-checkout variant of the turbo-v2 method widget, picked by TurboPaymentWidget when
 * isQuickCheckoutEnabled is set. Separate from the v2 widget because AIP diverges in ways that are
 * not toggles: no turbo strip, no POD consent half-card, no bill-to-company, no KFS disclaimer, an
 * inline loading state instead of a shimmer, and place-order runs through the webview-resize
 * handler. Pay params are shared via getTurboPayExtraParams.
 *
 * Pay is always single-tap here: AIP is never served a CVV-required saved card as the turbo
 * method, so there is no CVV bottom sheet.
 */
function TurboPaymentMethodWidget(props: IQuickCheckoutTurboPaymentMethodProps) {
    const {
        methodData,
        turboPaymentCtaMeta,
        paymentActions,
        onClickChangePaymentCta,
        balanceValue,
        showDiscountedAmount,
        isLoading,
        isLoadingForTheFirstTime,
        totalItemCount,
    } = props;

    const {
        iconUrl,
        methodDisplayName,
        promoMsg,
        discount,
        balanceMeta,
        showInlineBalance,
        cardSubName,
        showNewLineBalance,
        payLabel,
    } = getTurboPaymentPresentationalInfo(methodData, balanceValue);
    const { onSlideButtonSuccess } = useContext(TurboPaymentContext);
    const { handleQuickCheckoutToTurboPlaceOrder } = useQuickCheckoutNavHandlers();
    const screenWidthSize = useScreenWidthSize();
    const shouldStopAIPcontainerWidth = useMemo(() => isCompactScreenWidth(screenWidthSize), [screenWidthSize]);

    const shouldShowLoadingState = useMemo(
        () => !!isLoading && !isLoadingForTheFirstTime,
        [isLoading, isLoadingForTheFirstTime],
    );

    const onPay = useCallback((): void => {
        // Mirror gotoPayment / placeOrderFromCart: turbo pay skips those handlers, so set the
        // flag here. Needed when turbo fails → payments, then back / interrupt returns to QC.
        if (typeof window !== "undefined" && typeof sessionStorage !== "undefined") {
            sessionStorage.removeItem("isFromMROFoodCart");
            sessionStorage.setItem("isFromQuickCheckout", "true");
        }

        paymentActions.onPay(methodData, getTurboPayExtraParams(methodData, paymentActions), {
            isTurboPaymentMethod: true,
            isTurboV2: true,
        });
    }, [methodData, paymentActions]);

    const onPayButtonClick = useCallback(() => {
        onSlideButtonSuccess?.();

        // No CVV prompt and no POD half-card on quick checkout — place order directly.
        handleQuickCheckoutToTurboPlaceOrder(onPay);
    }, [onPay, onSlideButtonSuccess, handleQuickCheckoutToTurboPlaceOrder]);

    const handlePaymentChangeCtaClick = useCallback(() => {
        if (shouldShowLoadingState) {
            return;
        }
        onClickChangePaymentCta();
    }, [onClickChangePaymentCta, shouldShowLoadingState]);

    return (
        <Container>
            <PaymentMethodContainer
                shouldStopAIPcontainerWidth={shouldStopAIPcontainerWidth}
                isQuickCheckout={true}
                shouldShowLoadingState={shouldShowLoadingState}
            >
                <div
                    onClick={handlePaymentChangeCtaClick}
                    data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                    aria-label={CHANGE_PAYMENT_CTA_ARIA_LABEL}
                >
                    <Flex>
                        <PaymentIconV2
                            aria-hidden="true"
                            data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAYMENT_ICON}
                        >
                            <img alt="" crossOrigin="anonymous" src={iconUrl} height={12} />
                        </PaymentIconV2>
                        <StyledText
                            isQuickCheckout={true}
                            maxLines={1}
                            variant="V2.overline.neutral.medium"
                            color="text_Low_Emphasis"
                        >
                            {payLabel}
                        </StyledText>
                        <GenericIcon fillColor="text_Low_Emphasis" Icon={chevronUpFilled} />
                    </Flex>

                    <TurboPaymentDisplayName
                        displayName={methodDisplayName}
                        inlineBalanceMeta={showInlineBalance ? balanceMeta : undefined}
                        cardSubName={cardSubName}
                    />
                </div>

                <TurboPaymentMethodMetaWidget
                    variant={TurboPaymentVariant.QUICK_CHECKOUT}
                    promoMsg={promoMsg}
                    discount={discount}
                    newLineBalanceMeta={showNewLineBalance ? balanceMeta : undefined}
                />
            </PaymentMethodContainer>

            <PaymentMethodPayButtonContainer
                shouldStopAIPcontainerWidth={shouldStopAIPcontainerWidth}
                isQuickCheckout={true}
            >
                <QuickCheckoutTurboV2PaymentButton
                    finalAmount={turboPaymentCtaMeta?.amountWithDiscount || ""}
                    totalItemCount={totalItemCount}
                    onPayClick={onPayButtonClick}
                    shouldShowLoadingState={shouldShowLoadingState}
                    discountAmount={
                        showDiscountedAmount && turboPaymentCtaMeta?.amountWithoutDiscount
                            ? format(SLIDE_TO_PAY_AMOUNT, turboPaymentCtaMeta.amountWithoutDiscount)
                            : undefined
                    }
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON}
                />
            </PaymentMethodPayButtonContainer>
        </Container>
    );
}

export default memo(TurboPaymentMethodWidget);
